// src/services/api.ts
// Auth API source, fetches using VITE_APP_BASE_API

// mock开关，默认关闭
export const MOCK_ENABLED = false;

const BASE_URL = (import.meta.env.VITE_APP_BASE_API as string | undefined) || '/api';

import { emitForcePasswordChange } from '@/services/forcePasswordChange';
import { buildSeataTxHeaders, appendSeataTxQuery } from '@/services/seataTxHolder';
import {
    buildRequestCanonical,
    buildResponseCanonical,
    decryptAesGcm,
    encryptAesGcm,
    encryptPathToken,
    isCryptoEnvelope,
    pemOrSpkiToArrayBuffer,
    randomId,
    sha256Base64,
    signCanonical,
    verifyCanonical,
    type CryptoEnvelope,
} from '@/utils/apiCrypto';
import {
    clearCryptoSession,
    getActiveCryptoSession,
    isPayloadCipherEnabled,
} from '@/services/cryptoSession';
import userStore from '@/store/admin/userStore';
import { emitSessionExpired } from '@/services/sessionExpired';

function clearAllClientStorageAndCookies() {
    try {
        localStorage.clear();
        sessionStorage.clear();
    } catch {
        // ignore
    }
    try {
        if (typeof document !== 'undefined') {
            const cookies = document.cookie ? document.cookie.split(';') : [];
            for (const cookie of cookies) {
                const eqPos = cookie.indexOf('=');
                const name = (eqPos > -1 ? cookie.substring(0, eqPos) : cookie).trim();
                if (!name) continue;
                document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
            }
        }
    } catch {
        // ignore
    }
}

/**
 * Handle backend SYSTEM_NOT_LOGIN (302): clear client auth and notify UI (MUI Dialog).
 * Redirect to /login is owned by SessionExpiredDialog (Confirm or 10s countdown).
 */
export function handleSessionExpired(code?: number, msg?: string) {
    if (typeof window !== 'undefined' && window.location.pathname === '/login') {
        return;
    }

    try {
        userStore.clearUser();
    } catch {
        // ignore
    }
    try {
        clearCryptoSession();
    } catch {
        // ignore
    }
    try {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('Admin-Token');
        localStorage.removeItem('adminUserInfo');
        localStorage.removeItem('adminPermissions');
    } catch {
        // ignore
    }

    emitSessionExpired({ code, message: msg });
}

function redirectToLogin() {
    clearAllClientStorageAndCookies();
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.assign('/login');
    }
}

type ApiResultBody = { code?: number; msg?: string; message?: string; data?: unknown };

/** 从 Result JSON 或原始文本中提取可读错误信息 */
export function extractApiErrorMessage(body: unknown, fallback = '接口返回错误'): string {
    if (body && typeof body === 'object') {
        const result = body as ApiResultBody;
        if (typeof result.msg === 'string' && result.msg.trim()) return result.msg.trim();
        if (typeof result.message === 'string' && result.message.trim()) return result.message.trim();
    }
    if (typeof body === 'string' && body.trim()) {
        const trimmed = body.trim();
        try {
            const parsed = JSON.parse(trimmed);
            if (parsed && typeof parsed === 'object') {
                return extractApiErrorMessage(parsed, fallback);
            }
        } catch {
            // 非 JSON 文本，直接返回
        }
        return trimmed;
    }
    return fallback;
}

async function readResponseBody(res: Response): Promise<unknown> {
    const text = await res.text();
    if (!text) return null;
    try {
        return JSON.parse(text);
    } catch {
        return text;
    }
}

/** 解析非 2xx 响应体，返回可读错误信息 */
export async function getHttpErrorMessage(res: Response, fallback: string): Promise<string> {
    const body = await readResponseBody(res);
    return extractApiErrorMessage(body, fallback);
}

function throwFromApiBody(body: unknown, fallback: string): never {
    const result = (body && typeof body === 'object' ? body : null) as ApiResultBody | null;
    const msg = extractApiErrorMessage(body, fallback);
    const code = typeof result?.code === 'number' ? result.code : undefined;

    if (code === 303) {
        try {
            emitForcePasswordChange({ message: msg });
        } catch {
            // ignore
        }
        const err: any = new Error(msg);
        err.code = 303;
        throw err;
    }
    if (code === 302) {
        // Redis token missing/expired → gateway returns SYSTEM_NOT_LOGIN
        handleSessionExpired(code, msg);
        const err: any = new Error(msg);
        err.code = 302;
        throw err;
    }
    // UAT cipher: path decrypt may surface 4601 when login token is already gone/expired
    // before PermissionFilter can return 302 — still show SessionExpiredDialog.
    if (code === 4601) {
        handleSessionExpired(302, msg);
        const err: any = new Error(msg);
        err.code = 302;
        throw err;
    }
    if (code === 401) {
        const err: any = new Error(msg);
        err.code = 401;
        throw err;
    }

    const err: any = new Error(msg);
    if (code !== undefined) err.code = code;
    if (body && typeof body === 'object') err.raw = body;
    throw err;
}

function splitPathQuery(pathWithQuery: string): { path: string; query: string } {
    const q = pathWithQuery.indexOf('?');
    if (q < 0) return { path: pathWithQuery, query: '' };
    return { path: pathWithQuery.slice(0, q), query: pathWithQuery.slice(q + 1) };
}

function isCryptoPlainWhitelist(logicalPath: string): boolean {
    const p = logicalPath.toLowerCase();
    return (
        p.includes('/login') ||
        p.includes('/logout') ||
        p.includes('/register') ||
        p.includes('/crypto/server-public-key') ||
        p === '/auth/crypto/session' ||
        p.startsWith('/auth/crypto/session?') ||
        p.includes('/health') ||
        p.includes('/actuator')
    );
}

function headersToRecord(headers?: HeadersInit): Record<string, string> {
    const out: Record<string, string> = {};
    if (!headers) return out;
    if (headers instanceof Headers) {
        headers.forEach((v, k) => {
            out[k] = v;
        });
        return out;
    }
    if (Array.isArray(headers)) {
        for (const [k, v] of headers) out[k] = v;
        return out;
    }
    return { ...headers };
}

/**
 * Low-level fetch with optional path/body cipher. Prefer {@link fetchAPI} for Result unwrap.
 */
export async function authorizedFetch(logicalPath: string, options?: RequestInit): Promise<Response> {
    const token =
        typeof localStorage !== 'undefined'
            ? localStorage.getItem('adminToken') || localStorage.getItem('Admin-Token')
            : null;
    const pathWithQuery = appendSeataTxQuery(logicalPath);
    const { path: logicalOnly, query } = splitPathQuery(pathWithQuery);
    const method = (options?.method || 'GET').toUpperCase();
    const baseHeaders = headersToRecord(options?.headers);
    const mergedHeaders: Record<string, string> = {
        ...(token ? { 'Admin-Token': token } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...buildSeataTxHeaders(),
        ...baseHeaders,
    };

    const cipherOn = isPayloadCipherEnabled() && !isCryptoPlainWhitelist(logicalOnly);
    let url = `${BASE_URL}${pathWithQuery}`;
    let body = options?.body;
    let wirePath = logicalOnly;

    if (cipherOn) {
        let session = await getActiveCryptoSession();
        if (!session && token) {
            try {
                const { negotiateCryptoSession } = await import('./cryptoSession');
                session = await negotiateCryptoSession();
            } catch {
                session = null;
            }
        }
        if (!session) {
            // Token present but negotiate failed (often expired login) → same UX as SYSTEM_NOT_LOGIN.
            if (token) {
                handleSessionExpired(302, 'Your session has expired. Please log in again.');
            }
            const err: any = new Error('Crypto session required');
            err.code = 302;
            throw err;
        }

        const pathToken = await encryptPathToken(session.aesKey, logicalOnly);
        wirePath = `/c/${pathToken}`;
        url = query ? `${BASE_URL}${wirePath}?${query}` : `${BASE_URL}${wirePath}`;

        const contentType = mergedHeaders['Content-Type'] || mergedHeaders['content-type'] || '';
        const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
        if (isFormData) {
            delete mergedHeaders['Content-Type'];
            delete mergedHeaders['content-type'];
        }
        const hasBody =
            body != null &&
            body !== '' &&
            ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method) &&
            !isFormData &&
            !(typeof Blob !== 'undefined' && body instanceof Blob);

        let wireBodyBytes: Uint8Array = new Uint8Array(0);
        if (hasBody && typeof body === 'string') {
            const envelope = await encryptAesGcm(session.aesKey, new TextEncoder().encode(body));
            const envelopeJson = JSON.stringify(envelope);
            body = envelopeJson;
            wireBodyBytes = new Uint8Array(new TextEncoder().encode(envelopeJson));
            mergedHeaders['Content-Type'] = 'application/json';
        } else if (hasBody && body instanceof ArrayBuffer) {
            const envelope = await encryptAesGcm(session.aesKey, new Uint8Array(body));
            const envelopeJson = JSON.stringify(envelope);
            body = envelopeJson;
            wireBodyBytes = new Uint8Array(new TextEncoder().encode(envelopeJson));
            mergedHeaders['Content-Type'] = 'application/json';
        }

        const requestId = randomId();
        const timestamp = String(Date.now());
        const nonce = randomId();
        const bodyDigest = await sha256Base64(wireBodyBytes);
        // WHY: Sign raw Admin-Token (not "Bearer …"). Gateway may omit Authorization
        // downstream; Admin-Token is always relayed and matches backend verify candidates.
        const authorization =
            mergedHeaders['Admin-Token'] ||
            (mergedHeaders['Authorization'] || '').replace(/^Bearer\s+/i, '') ||
            '';
        const contentTypeForSign = 'application/json';
        void contentType;
        const canonical = buildRequestCanonical({
            method,
            wirePath,
            query,
            authorization,
            sessionId: session.sessionId,
            requestId,
            timestamp,
            nonce,
            contentType: contentTypeForSign,
            bodyDigestBase64: bodyDigest,
        });
        const signature = await signCanonical(session.clientPrivateKey, canonical);

        mergedHeaders['X-Crypto-Session-Id'] = session.sessionId;
        mergedHeaders['X-Request-Id'] = requestId;
        mergedHeaders['X-Timestamp'] = timestamp;
        mergedHeaders['X-Nonce'] = nonce;
        mergedHeaders['X-Signature'] = signature;
        mergedHeaders['X-Key-Id'] = session.keyId;
        mergedHeaders['X-Crypto-Wire-Path'] = wirePath;

        const res = await fetch(url, {
            credentials: 'include',
            ...options,
            method,
            body,
            headers: mergedHeaders,
        });

        return maybeDecryptResponse(res, session, requestId, nonce);
    }

    if (!mergedHeaders['Content-Type'] && !mergedHeaders['content-type'] && typeof body === 'string') {
        mergedHeaders['Content-Type'] = 'application/json';
    }

    return fetch(url, {
        credentials: 'include',
        ...options,
        method,
        body,
        headers: mergedHeaders,
    });
}

async function maybeDecryptResponse(
    res: Response,
    session: NonNullable<Awaited<ReturnType<typeof getActiveCryptoSession>>>,
    requestId: string,
    nonce: string,
): Promise<Response> {
    const ct = res.headers.get('Content-Type') || '';
    if (!ct.toLowerCase().includes('json')) {
        return res;
    }
    const text = await res.text();
    if (!text) {
        return new Response(null, { status: res.status, statusText: res.statusText, headers: res.headers });
    }
    let parsed: unknown;
    try {
        parsed = JSON.parse(text);
    } catch {
        return new Response(text, { status: res.status, statusText: res.statusText, headers: res.headers });
    }

    // Business Result error without envelope (e.g. crypto errors)
    if (parsed && typeof parsed === 'object' && 'code' in (parsed as object) && !isCryptoEnvelope(parsed)) {
        return new Response(text, { status: res.status, statusText: res.statusText, headers: res.headers });
    }

    if (!isCryptoEnvelope(parsed)) {
        return new Response(text, { status: res.status, statusText: res.statusText, headers: res.headers });
    }

    const envelope = parsed as CryptoEnvelope;
    const encBytes = new TextEncoder().encode(text);
    const pathCipher = res.headers.get('X-Crypto-Path') || '';
    const signature = res.headers.get('X-Signature');
    if (signature && session.serverPublicKeyPem) {
        let verifyKey = session.serverVerifyKey;
        if (!verifyKey) {
            verifyKey = await crypto.subtle.importKey(
                'spki',
                pemOrSpkiToArrayBuffer(session.serverPublicKeyPem),
                { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' },
                false,
                ['verify'],
            );
            session.serverVerifyKey = verifyKey;
        }
        const respDigest = await sha256Base64(encBytes);
        const canonical = buildResponseCanonical({
            requestId: res.headers.get('X-Request-Id') || requestId,
            nonce,
            bodyDigestBase64: respDigest,
            pathCipher,
        });
        const ok = await verifyCanonical(verifyKey, canonical, signature);
        if (!ok) {
            const err: any = new Error('Response signature invalid');
            err.code = 4602;
            throw err;
        }
    }

    const plain = await decryptAesGcm(session.aesKey, envelope);
    const plainText = new TextDecoder().decode(plain);
    // Path echo is optional integrity hint; skip decrypt on hot path (AES already authenticated body).
    const headers = new Headers(res.headers);
    headers.set('Content-Type', 'application/json');
    return new Response(plainText, { status: res.status, statusText: res.statusText, headers });
}

export async function fetchAPI<T = any>(path: string, options?: RequestInit): Promise<T> {
    if (MOCK_ENABLED) {
        throw new Error('MOCK_ENABLED: 当前为mock模式，未实现mock数据');
    }
    const res = await authorizedFetch(path, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...(options?.headers || {}),
        },
    });
    if (!res.ok) {
        const body = await readResponseBody(res);
        if (res.status === 500) {
            if (body && typeof body === 'object' && typeof (body as ApiResultBody).code === 'number') {
                throwFromApiBody(body, 'Server error, please login again');
            }
            redirectToLogin();
            const err: any = new Error('Server error, please login again');
            err.status = 500;
            throw err;
        }
        throwFromApiBody(body, 'Auth API Error');
    }
    const result = await res.json();
    if (result?.code !== 0 && result?.code !== undefined) {
        throwFromApiBody(result, '接口返回错误');
    }
    return result.data;
}

export const BASE_AUTH_API = '/auth';
export const BASE_ADMIN_API = '/admin';
export const BASE_ACCOUNT_API = '/account';
export const BASE_AUDIT_API = '/audit';
